<template>
<div class="search-component">
  <button v-if="!active" @click="toggleSearch" class="search-component__open-button">
    <i class="fal fa-search"></i>
  </button>
  <template v-if="active">
    <div class="search-input-wrapper" :class="{ 'search-is-active': loading }">
      <!-- Using `:value` + `@input` so the search is triggered on every press on mobile.
                See https://github.com/vuejs/vue/issues/9299 -->
      <input type="text"
             class="form-control search-input"
             :id="id"
             :placeholder="searchInputPlaceholder"
             :value="query"
             @input="processInput"
             @keyup.enter="handleEnterKey"
             @keyup.esc="clearSearch"
             @keyup.up="handleUpKey"
             @keyup.down="handleDownKey"
             @keyup.left="handleLeftKey"
             @keyup.right="handleRightKey"
      >
      <span class="cancel-search-btn" @click="clearSearch">
        &times;
      </span>
      <div v-if="!(queryTooShort || (loading && !hasResults))" class="search-results-wrapper">
        <Simplebar class="search-results-scroll-container" v-if="hasResults" >
          <div v-if="hasCategoriesResults" class="search-results-list search-results-categories-list">
            <h3 class="search-results-list-heading">Похожие категории</h3>
            <ul>
              <li
                v-for="(category, index) in suggestions.categories"
                :key="category.id"
                :ref="`search-result-category-${category.id}`"
              >
                <router-link :to="categoryRouteParams(category)"
                             @click.native="clearSearch"
                             :class="{ active: isSelectedItem('categories', index) }"
                >
                  <span class="search-result-header">
                    <span class="search-result-name"
                    >{{ category.name }}</span>
                  </span>
                </router-link>
              </li>
            </ul>
          </div>
          <div v-if="hasProductsResults" class="search-results-list search-results-products-list">
            <h3 class="search-results-list-heading">Похожие товары</h3>
            <ul>
              <li
                v-for="(item, index) in suggestions.products"
                :key="item.variant.id"
                :ref="`search-result-product-${item.variant.id}`"
              >
                <router-link :to="productRouteParams(item)"
                             @click.native="clearSearch"
                             :class="{ active: isSelectedItem('products', index) }"
                >
                  <span class="search-result-header">
                    <span class="search-result-name"
                    >{{ item.product.name }}</span>
                    <span class="search-result-description"
                    >{{ productVariantDescription(item) }}</span>
                  </span>
                  <span class="search-result-product-actions">
                    <span class="search-results-product-price"
                    >{{ priceToText(item.variant.price) }}</span>
                    <span
                      @click.prevent.stop="addProductVariant({ variantId: item.variant.id })"
                      @click.right.prevent="() => {
                        if (isInCart(item.variant.id)) {
                          removeProductVariant({ variantId: item.variant.id })
                        }
                      }"
                      class="product-variant-quantity"
                      :class="isInCart(item.variant.id)
                        ? 'product-variant-quantity--in-cart'
                        : 'product-variant-quantity--not-in-cart'
                      "
                    >
                      <template v-if="isInCart(item.variant.id)">
                        {{ cartQuantity(item.variant.id) }}
                      </template>
                      <IconBasket v-else/>
                    </span>
                  </span>
                </router-link>
              </li>
            </ul>
          </div>
        </Simplebar>
        <div class="search-summary">
          <i class="search-summary-icon fal fa-search"/>
          <span class="search-summary-text">
            <template v-if="!hasResults"
            >Ничего не найдено</template>
            <template v-else
            >{{ numProductsFoundText }}. Используйте клавиши ↓↑ ←→ и Enter для навигации.</template>
          </span>
        </div>
      </div>
      <span class="loading-spinner" v-if="loading">
        <i class="fal fa-spinner fa-spin"></i>
      </span>
    </div>
  </template>
</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import axios from '@/zenky'
// Using ponyfill smooth scrolling mainly because `scroll-behavior: smooth;` does not work well with Simplebar.
// https://github.com/Grsmto/simplebar/issues/261
// Also because it's more customizable.
import scrollIntoView from 'smooth-scroll-into-view-if-needed'
import AsyncSimplebar from '@/components/AsyncSimplebar.js';
import config from '@/config'
import { variantDescription, transformProductsToVariants } from '@/helpers/products'
import SearchHandler from '@/helpers/SearchHandler'
import SearchResultsSelection from './SearchResultsSelection';
import pluralize from '@/helpers/pluralize'
import priceToText from '@/helpers/priceToText'

export default {
  $searchInput: null,
  $selection: null,

  components: {
    Simplebar: AsyncSimplebar,
    IconBasket: () => import('@/assets/images/icons/icon-basket.svg?inline'),
  },

  props: {
    active: {
      type: Boolean,
      default: true,
    },
  },

  created() {
    this.$selection = new SearchResultsSelection()
  },

  mounted() {
    this.$searchInput = document.getElementById(this.id)
  },

  computed: {
    ...mapGetters({
      searchInputPlaceholder: 'settings/searchInputPlaceholder',
      hasOrderVariants: 'order/hasVariants',
      orderVariants: 'order/variants',
    }),

    queryTooShort() {
      return !this.query.trim();
    },

    loading() {
      return !this.searchHandler.idle;
    },

    hasResults() {
      return this.hasCategoriesResults || this.hasProductsResults
    },

    hasCategoriesResults() {
      return this.suggestions.categories.length > 0
    },

    hasProductsResults() {
      return this.suggestions.products.length > 0
    },

    numProductsFoundText() {
      return pluralize(this.suggestions.products.length, ['Найден', 'Найдено', 'Найдено'], false)
                + ' ' + pluralize(this.suggestions.products.length, ['товар', 'товара', 'товаров']);
    },

    categorySelectionIsActive() {
      return this.selection.index >= 0 && this.selection.type === 'categories'
    },

    productSelectionIsActive() {
      return this.selection.index >= 0 && this.selection.type === 'products'
    },

    selectedCategory() {
      if (!this.categorySelectionIsActive) {
        return null
      }

      if (typeof this.suggestions.categories[this.selection.index] === 'undefined') {
        return null
      }

      return this.suggestions.categories[this.selection.index]
    },

    selectedProduct() {
      if (!this.productSelectionIsActive) {
        return null
      }

      return this.suggestions.products[this.selection.index]
    },

    selectedProductVariantId() {
      if (!this.productSelectionIsActive) {
        return null
      }

      return this.selectedProduct.variant.id
    },
  },

  methods: {
    ...mapActions({
      searchCategories: 'catalog/searchCategories',
      addProductVariant: 'order/addProductVariant',
      removeProductVariant: 'order/removeProductVariant',
    }),

    toggleSearch() {
      this.$emit('update:active', !this.active);
    },

    processInput(e) {
      this.query = e.target.value;
      if (this.queryTooShort) {
        this.suggestions.products = []
        this.suggestions.categories = []
        return;
      }
      this.searchHandler.search(this.query)
    },

    dispatchCategoriesSearch(query) {
      return new Promise(resolve => {
        this.suggestions.categories = []
        this.searchCategories(query).then(categories => {
          this.suggestions.categories = categories
          resolve()
        })
      })
    },

    clearSearch() {
      this.query = '';
      this.$emit('clear');
    },

    isSelectedItem(type, index) {
      return this.selection.type === type && this.selection.index === index
    },

    resetSelection() {
      this.$selection.setItems({
        categories: this.suggestions.categories,
        products: this.suggestions.products,
      })

      this.selection = {
        type: '',
        index: -1,
        maxIndex: 0,
      }
    },

    handleEnterKey() {
      if (this.selection.index === -1) {
        return
      }

      switch (this.selection.type) {
        case 'categories':
          return this.$router.push(this.categoryRouteParams(this.selectedCategory))
        case 'products':
          return this.$router.push(this.productRouteParams(this.selectedProduct))
      }
    },

    handleUpKey() {
      this.selection = this.$selection.up()
    },

    handleDownKey() {
      this.selection = this.$selection.down()
    },

    handleLeftKey() {
      if (!this.productSelectionIsActive) {
        return
      } else if (!this.isInCart(this.selectedProductVariantId)) {
        return
      }

      this.removeProductVariant({ variantId: this.selectedProductVariantId })
    },

    handleRightKey() {
      if (!this.productSelectionIsActive) {
        return
      }

      this.addProductVariant({ variantId: this.selectedProductVariantId })
    },

    productRouteParams(item) {
      return {
        name: 'product',
        params: {
          productId: item.product.short_id,
          productSlug: item.product.slug,
        },
        query: {
          variant: item.variant.id,
        }
      }
    },

    categoryRouteParams(item) {
      return {
        name: 'catalog',
        params: {
          categoryId: item.id,
          categorySlug: item.slug,
        }
      }
    },

    productVariantDescription(item) {
      return variantDescription(item.variant)
    },

    cartVariantIndex(variantId) {
      if (!this.hasOrderVariants) {
        return -1
      }

      return this.orderVariants.findIndex(ov => ov.product_variant_id === variantId)
    },

    isInCart(variantId) {
      return this.cartVariantIndex(variantId) !== -1
    },

    cartQuantity(variantId) {
      const index = this.cartVariantIndex(variantId)

      if (index === -1) {
        return 0
      }

      return this.orderVariants[index].quantity
    },

    priceToText,
  },

  watch: {
    active(n) {
      if (!n) {
        this.query = ''
        this.suggestions.products = []
        this.suggestions.categories = []
      } else {
        this.$nextTick(() => document.getElementById(this.id).focus())
      }
    },
    selectedCategory(n) {
      if (n) {
        const el = this.$refs[`search-result-category-${n.id}`][0];
        scrollIntoView(el, {
          scrollMode: 'always',
          block: 'center',
          boundary: this.$el,
        });
      }
    },
    selectedProduct(n) {
      if (n) {
        const el = this.$refs[`search-result-product-${n.variant.id}`][0];
        // Same as in the category handler.
        scrollIntoView(el, {
          scrollMode: 'always',
          block: 'center',
          boundary: this.$el,
        });
      }
    }
  },

  data() {
    return {
      id: `search-input-${Math.random().toString().replace('.', '_')}`,
      query: '',
      lastQuery: '',
      searchHandler: new SearchHandler(
        query => axios.get('products', { params: {
          with: config.includes.appbarProductsSearch,
          search: query,
          count: 15,
          city_id: this.$store.state.store.activeCityId,
        } }),
        async res => {
          this.suggestions.products = transformProductsToVariants(res.data.data);
          await this.dispatchCategoriesSearch(this.query.trim());
          this.resetSelection();
        },
        500,
      ),
      suggestions: {
        categories: [],
        products: [],
      },
      selection: {
        type: '',
        index: -1,
        maxIndex: 0,
      }
    }
  }
}
</script>

<style lang="less">
.search-component {
  .search-input-wrapper {
    @apply relative mx-2;

    .loading-spinner {
      @apply absolute px-2 inset-y-0 flex items-center text-primary;
      right: 1.75rem;
    }

    .cancel-search-btn {
      @apply absolute cursor-pointer px-3 inset-y-0 right-0 flex items-center text-grey text-2xl font-hairline;
      padding-bottom: 0.3rem; // It's a bit skewed withing the line. To compensate it.
    }

    .search-results-wrapper {
      @apply absolute bg-white rounded border border-light-dark
        overflow-hidden; // For rounded borders.
      box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
      top: calc(100% + theme('margin.5'));
      width: 100%;

      .search-results-scroll-container {
        @apply overflow-auto;
        max-height: 315px;

        .simplebar-scrollbar {
          &::before {
            @apply bg-light-dark;
          }
          &.simplebar-visible::before {
            @apply opacity-100;
          }
        }
      }

      .search-summary {
        @apply p-3 font-light text-xs text-light-darkest;
        &:not(:first-child) {
          @apply border-t border-light-dark;
        }
        .search-summary-icon {
          @apply font-bold mr-2;
        }
      }

      .search-results-list {
        @apply border-b border-solid border-light-dark;

        &:last-child {
          @apply border-b-0 pb-0;
        }

        h3 {
          @apply mx-3 my-2 text-base font-normal opacity-80;
        }

        ul {
          li {
            @apply block w-full;
            a {
              @apply flex w-full px-3 py-2 bg-white text-black h-auto;
              transition: none;
              .search-result-header {
                @apply flex-grow flex flex-col justify-start;
                .search-result-name {
                  @apply font-light text-sm opacity-80;
                }
                .search-result-description {
                  @apply text-2xs font-light opacity-50;
                }
              }
              &:hover, &.active {
                @apply bg-primary-lightest;
                color: initial;
                .search-result-name {
                  @apply text-primary opacity-100;
                }
              }
            }
          }
        }
        &.search-results-products-list {
          .search-result-product-actions {
            @apply text-sm flex items-center;

            .search-results-product-price {
              @apply font-normal opacity-80 mx-3;
            }

            .product-variant-quantity {
              @apply inline-block h-6 rounded-full text-xs bg-white flex items-center justify-center;
              min-width: theme('width.6'); // So it's round.
              svg {
                @apply fill-current text-primary align-middle;
                height: 12px;
              }
              &--in-cart {
                @apply text-white bg-primary align-middle;
              }
            }
          }
        }
      }
    }
  }
}

@screen md {
  .search-component {
    .search-input-wrapper {
      .search-results-wrapper {
        width: 100%;
      }
    }
  }
}
</style>
